import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import {
  TERMINAL_STATUSES,
  CANCELLED_STATUSES,
  IN_PROGRESS_STATUSES,
} from '../../../constants/statuses';

/**
 * Customer-facing post-booking journey stepper.
 *
 * Read-only: polls the existing get_booking_via_actual_booking_id endpoint for
 * order_status (no new backend, no new process, no booking-write path). Maps the
 * raw status onto 5 customer steps using ONLY the known status constants in
 * constants/statuses.js (no fabricated status strings). Falls back gracefully to
 * the static confirmed steps if polling fails — never blocks the page.
 *
 * Props: bookingId (the actual_booking_id). If absent, renders the static
 * journey with step 1 active.
 */
const STEPS = [
  { key: 'booked', label: 'Booked', desc: 'Your service is confirmed.' },
  { key: 'assigned', label: 'Mechanic assigned', desc: 'A verified mechanic is allotted.' },
  { key: 'enroute', label: 'On the way', desc: 'Your mechanic reaches you in 30 minutes.' },
  { key: 'inprogress', label: 'Repaired in front of you', desc: 'Estimate confirmed before final work — you pay only what you approved.' },
  { key: 'done', label: 'Done', desc: 'Service complete with a 30-day warranty.' },
];

function stepIndexFor(status) {
  if (!status) return 0;
  const s = String(status).toUpperCase();
  if (CANCELLED_STATUSES.includes(status) || s.startsWith('CANCELLED')) return -1;
  if (
    status === 'COMPLETED' ||
    status === 'CASH TAKEN' ||
    status === 'SETTLED' ||
    TERMINAL_STATUSES.includes(status)
  )
    return 4;
  if (IN_PROGRESS_STATUSES.includes(status) || s.includes('IN PROGRESS')) return 3;
  if (s.includes('WAY') || s.includes('REACHED') || s.includes('ARRIV') || s.includes('TRANSIT'))
    return 2;
  if (s.includes('ASSIGN') || s.includes('ALLOCAT') || s.includes('ACCEPT')) return 1;
  return 0; // CONFIRMED / unknown → safely show "Booked"
}

const POLL_MS = 30000;
const MAX_POLLS = 120; // ~1h ceiling; stops earlier on terminal status

const BookingStatusStepper = ({ bookingId }) => {
  const [status, setStatus] = useState(null);
  const pollsRef = useRef(0);

  useEffect(() => {
    if (!bookingId || typeof window === 'undefined') return;
    let timer = null;
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      try {
        const res = await axios.get(
          `https://ridenrepair.com/api/get_booking_via_actual_booking_id?booking_id=${bookingId}`
        );
        const st = res && res.data && res.data.order_status;
        if (!cancelled && st) setStatus(st);
        const idx = stepIndexFor(st);
        pollsRef.current += 1;
        if (idx === 4 || idx === -1 || pollsRef.current >= MAX_POLLS) return; // stop
      } catch (e) {
        // ignore — keep showing last known / static steps
      }
      if (!cancelled) timer = setTimeout(tick, POLL_MS);
    };

    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [bookingId]);

  const idx = stepIndexFor(status);

  if (idx === -1) {
    return (
      <div className="rr-stepper rr-stepper--cancelled">
        <p>This booking was cancelled. Need help? We’re here for you.</p>
      </div>
    );
  }

  return (
    <div className="rr-stepper" aria-label="Service progress">
      {STEPS.map((s, i) => (
        <div
          key={s.key}
          className={`rr-stepper__step${i <= idx ? ' is-done' : ''}${
            i === idx ? ' is-active' : ''
          }`}
        >
          <div className="rr-stepper__dot">{i < idx ? '✓' : i + 1}</div>
          <div className="rr-stepper__body">
            <div className="rr-stepper__label">{s.label}</div>
            <div className="rr-stepper__desc">{s.desc}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default BookingStatusStepper;
